import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaApple, FaWindows, FaLinux } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import {
  LuDownload,
  LuRefreshCw,
  LuCheck,
  LuInfo,
  LuLoader,
  LuChrome,
  LuTerminal,
  LuUpload,
} from 'react-icons/lu';
import { toast } from 'react-toast';
import useAgentManagement from '../../hooks/useAgentManagement';
import UploadAgentModal from './UploadAgentModal';
import DeleteConfirmationModal from '../test-plans/DeleteConfirmationModal';
import { useLatestAgentVersion } from '../../hooks/API/settings.api';
import { formatDate } from '../../utils/dateTimeFormat';
import { Button } from '../ui';

const OS_DISPLAY_NAMES = {
  1: 'Linux',
  2: 'Windows',
  3: 'Mac OS',
};

const AGENT_TYPE_ROBITEST = 1;
const CONNECTION_TIMEOUT_MS = 5000;
const POLLING_INTERVAL_MS = 15000;

const INSTALL_STEPS = [
  {
    title: 'Download the Linux package',
    detail: 'Get the latest .deb for Ubuntu/Debian from the versions list below.',
  },
  {
    title: 'Install with the agent installer',
    detail: 'Place install_agent.sh next to the .deb, then run chmod +x install_agent.sh && ./install_agent.sh',
  },
  {
    title: 'Launch Robitest Agent',
    detail: 'Open it from Applications or run robitest in a terminal. Chrome launches with the extension preloaded.',
  },
  {
    title: 'Verify connection',
    detail: 'Use Check Connection to confirm the agent and Chrome extension are reachable.',
  },
];

const OS_OPTIONS = [
  { id: 1, label: 'Linux', icon: FaLinux, available: true, badge: '.deb' },
  { id: 2, label: 'Windows', icon: FaWindows, available: false, badge: 'Soon' },
  { id: 3, label: 'macOS', icon: FaApple, available: false, badge: 'Soon' },
];

const AgentInstallation = () => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedOS, setSelectedOS] = useState(1);
  const [deletingAgentId, setDeletingAgentId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);

  const [isChecking, setIsChecking] = useState(false);
  const [agentConnected, setAgentConnected] = useState(false);
  const [extensionConnected, setExtensionConnected] = useState(false);
  const [liveAgentVersion, setLiveAgentVersion] = useState(null);
  const [liveExtensionVersion, setLiveExtensionVersion] = useState(null);

  const timeoutIdRef = useRef(null);
  const silentRef = useRef(true);

  const { agents, loading, error, fetchAgents, uploadAgent, deleteAgent, OS_TYPES } =
    useAgentManagement();

  const { data: latestAgentVersion, isLoading: latestLoading } = useLatestAgentVersion({
    osId: OS_TYPES.LINUX,
    agentType: AGENT_TYPE_ROBITEST,
  });

  const clearConnectionTimeout = useCallback(() => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  }, []);

  const checkConnection = useCallback(
    (silent = false) => {
      silentRef.current = silent;
      clearConnectionTimeout();
      if (!silent) setIsChecking(true);

      window.postMessage(
        { source: 'ROBITEST_CLOUD', action: 'CHECK_CONNECTIVITY' },
        window.location.origin
      );

      timeoutIdRef.current = setTimeout(() => {
        setIsChecking(false);
        setAgentConnected(false);
        setExtensionConnected(false);
        if (!silentRef.current) {
          toast.error('Connection timed out — ensure the agent is running');
        }
      }, CONNECTION_TIMEOUT_MS);
    },
    [clearConnectionTimeout]
  );

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== window) return;
      const { source, type, agent_version, extension_version } = event.data || {};
      if (source !== 'ROBITEST_AGENT') return;

      clearConnectionTimeout();
      setIsChecking(false);

      if (type === 'CONNECTIVITY_OK') {
        setAgentConnected(true);
        setExtensionConnected(true);
        if (agent_version) setLiveAgentVersion(agent_version);
        if (extension_version) setLiveExtensionVersion(extension_version);
        if (!silentRef.current) toast.success('Agent and extension connected');
      } else if (type === 'CONNECTIVITY_FAILED') {
        setAgentConnected(false);
        setExtensionConnected(false);
        if (!silentRef.current) toast.error('Failed to connect to agent or extension');
      }
    };

    window.addEventListener('message', handleMessage);
    checkConnection(true);
    const interval = setInterval(() => checkConnection(true), POLLING_INTERVAL_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearConnectionTimeout();
      clearInterval(interval);
    };
  }, [checkConnection, clearConnectionTimeout]);

  useEffect(() => {
    fetchAgents(OS_TYPES.LINUX).catch(() => {});
  }, [fetchAgents, OS_TYPES.LINUX]);

  const downloadAgent = (downloadUrl, agentVersion) => {
    try {
      if (!downloadUrl) {
        toast.error('Download URL not available');
        return;
      }
      const link = document.createElement('a');
      link.href = encodeURI(downloadUrl);
      link.download = `Robitest_agent_${agentVersion?.trim() || 'unknown'}.deb`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } catch {
      toast.error('Failed to download agent');
    }
  };

  const handleDownloadLatest = () => {
    const url = latestAgentVersion?.agent_path || latestAgentVersion?.download_url;
    const version = latestAgentVersion?.version;
    if (!url) {
      toast.error('Latest agent package is not available yet');
      return;
    }
    downloadAgent(url, version);
  };

  const handleDeleteAgent = (agentId) => {
    setAgentToDelete(agentId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;
    setDeletingAgentId(agentToDelete);
    setShowDeleteConfirm(false);
    try {
      await deleteAgent(agentToDelete, selectedOS);
      toast.success('Agent version removed');
      setAgentToDelete(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete agent');
    } finally {
      setDeletingAgentId(null);
    }
  };

  const cancelDeleteAgent = () => {
    setShowDeleteConfirm(false);
    setAgentToDelete(null);
  };

  const handleUploadAgent = async (file, osId, version) => {
    try {
      await uploadAgent(file, osId, version);
      toast.success(`Agent ${version} uploaded successfully`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to upload agent');
      throw err;
    }
  };

  const linuxAgents = agents[OS_TYPES.LINUX] || [];
  const bothConnected = agentConnected && extensionConnected;
  const latestId = latestAgentVersion?.id;
  const latestVersionLabel = latestAgentVersion?.version
    ? `v${latestAgentVersion.version}`
    : null;

  return (
    <div className="settings-section agents-settings">
      <div className="settings-section-intro">
        <h2 className="settings-section-title">Agents</h2>
        <p className="settings-subtitle">
          Download, install, and verify the local Robitest agent for recording and execution.
        </p>
      </div>

      {/* Connection status */}
      <div
        className={`agents-status ${bothConnected ? 'agents-status--ok' : 'agents-status--pending'}`}
      >
        <div className="agents-status__main">
          {bothConnected ? (
            <LuCheck size={20} className="agents-status__icon agents-status__icon--ok" />
          ) : (
            <LuInfo size={20} className="agents-status__icon" />
          )}
          <div>
            <div className="agents-status__title">
              {bothConnected ? 'Agent ready' : 'Agent not detected'}
            </div>
            <div className="agents-status__subtitle">
              {bothConnected
                ? 'Local agent and Chrome extension are connected.'
                : 'Install and start the agent, then verify the connection.'}
            </div>
          </div>
        </div>
        <div className="agents-status__meta">
          <span className={`agents-pill ${agentConnected ? 'agents-pill--ok' : ''}`}>
            {agentConnected ? <LuCheck size={12} /> : <span className="agents-pill__dot" />}
            Agent{liveAgentVersion ? ` ${liveAgentVersion}` : ''}
          </span>
          <span className={`agents-pill ${extensionConnected ? 'agents-pill--ok' : ''}`}>
            {extensionConnected ? <LuCheck size={12} /> : <LuChrome size={12} />}
            Extension{liveExtensionVersion ? ` ${liveExtensionVersion}` : ''}
          </span>
          <Button
            variant="secondary"
            size="sm"
            icon={isChecking ? <LuLoader className="agents-spin" size={14} /> : <LuRefreshCw size={14} />}
            onClick={() => checkConnection(false)}
            disabled={isChecking}
          >
            {isChecking ? 'Checking…' : 'Check Connection'}
          </Button>
        </div>
      </div>

      <div className="agents-grid">
        {/* Download */}
        <section className="agents-card">
          <div className="agents-card__header">
            <h3 className="agents-card__title">Download</h3>
            <p className="agents-card__desc">
              Get the Robitest agent package for your machine. Linux is available now.
            </p>
          </div>

          <div className="agents-os-grid">
            {OS_OPTIONS.map(({ id, label, icon: Icon, available, badge }) => (
              <button
                key={id}
                type="button"
                className={`agents-os-card ${selectedOS === id ? 'agents-os-card--active' : ''} ${
                  !available ? 'agents-os-card--disabled' : ''
                }`}
                disabled={!available}
                onClick={() => available && setSelectedOS(id)}
                title={available ? `Select ${label}` : 'Coming soon'}
              >
                <Icon size={22} className={id === 1 ? 'agents-os-card__icon--linux' : ''} />
                <span className="agents-os-card__label">{label}</span>
                <span className="agents-os-card__badge">{badge}</span>
              </button>
            ))}
          </div>

          <div className="agents-download-cta">
            <div>
              <div className="agents-download-cta__label">Latest Linux package</div>
              <div className="agents-download-cta__version">
                {latestLoading
                  ? 'Checking…'
                  : latestVersionLabel || 'No release published yet'}
              </div>
            </div>
            <Button
              variant="primary"
              size="md"
              icon={<LuDownload size={16} />}
              onClick={handleDownloadLatest}
              disabled={latestLoading || !latestAgentVersion}
            >
              Download Latest
            </Button>
          </div>
        </section>

        {/* Install steps */}
        <section className="agents-card">
          <div className="agents-card__header">
            <h3 className="agents-card__title">Install</h3>
            <p className="agents-card__desc">
              Clean install or upgrade using the agent installer from the agent repo.
            </p>
          </div>
          <ol className="agents-steps">
            {INSTALL_STEPS.map((step, index) => (
              <li key={step.title} className="agents-steps__item">
                <span className="agents-steps__num">{index + 1}</span>
                <div>
                  <div className="agents-steps__title">{step.title}</div>
                  <div className="agents-steps__detail">{step.detail}</div>
                </div>
              </li>
            ))}
          </ol>
          <div className="agents-terminal">
            <LuTerminal size={14} />
            <code>chmod +x install_agent.sh && ./install_agent.sh</code>
          </div>
        </section>
      </div>

      {/* Versions */}
      <section className="agents-card agents-card--full">
        <div className="agents-card__header agents-card__header--row">
          <div>
            <h3 className="agents-card__title">
              Available versions — {OS_DISPLAY_NAMES[selectedOS] || 'Linux'}
            </h3>
            <p className="agents-card__desc">
              Published packages for download. Latest is highlighted.
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            icon={<LuUpload size={14} />}
            onClick={() => setShowUploadModal(true)}
            disabled={loading}
          >
            Upload Version
          </Button>
        </div>

        {loading && linuxAgents.length === 0 && (
          <div className="agents-empty">
            <LuLoader className="agents-spin" size={20} />
            <span>Loading agent versions…</span>
          </div>
        )}

        {!loading && error && linuxAgents.length === 0 && (
          <div className="agents-empty">
            <LuInfo size={18} />
            <span>{error}</span>
            <Button variant="secondary" size="sm" onClick={() => fetchAgents(OS_TYPES.LINUX)}>
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && linuxAgents.length === 0 && (
          <div className="agents-empty">
            <LuInfo size={18} />
            <div>
              <div className="agents-empty__title">No packages yet</div>
              <div className="agents-card__desc">
                Upload a Linux .deb to publish the first agent version for this workspace.
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              icon={<LuUpload size={14} />}
              onClick={() => setShowUploadModal(true)}
            >
              Upload Version
            </Button>
          </div>
        )}

        {linuxAgents.length > 0 && (
          <div className="agents-table-wrap">
            <table className="agents-table">
              <thead>
                <tr>
                  <th>Version</th>
                  <th>OS</th>
                  <th>Published</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {linuxAgents.map((agent) => {
                  const isLatest = latestId != null && latestId === agent.id;
                  return (
                    <tr key={agent.id} className={isLatest ? 'agents-table__row--latest' : ''}>
                      <td>
                        <div className="agents-table__version">
                          <FaLinux className="agents-os-card__icon--linux" size={14} />
                          <span>v{agent.version}</span>
                          {isLatest && <span className="agents-badge-latest">Latest</span>}
                        </div>
                      </td>
                      <td>{OS_DISPLAY_NAMES[agent.os] || 'Linux'}</td>
                      <td>{formatDate(agent.created_at)}</td>
                      <td>
                        <div className="agents-table__actions">
                          <Button
                            variant="primary"
                            size="sm"
                            icon={<LuDownload size={14} />}
                            onClick={() => downloadAgent(agent.download_url, agent.version)}
                            disabled={loading || deletingAgentId === agent.id}
                          >
                            Download
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            icon={<MdDelete size={14} />}
                            onClick={() => handleDeleteAgent(agent.id)}
                            disabled={loading || deletingAgentId === agent.id}
                            aria-label={`Delete agent v${agent.version}`}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <UploadAgentModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUpload={handleUploadAgent}
        isLoading={loading}
        selectedOS={selectedOS}
      />

      <DeleteConfirmationModal
        isOpen={showDeleteConfirm}
        itemName={`Agent v${linuxAgents.find((a) => a.id === agentToDelete)?.version || ''}`}
        itemType="agent version"
        onCancel={cancelDeleteAgent}
        onConfirm={confirmDeleteAgent}
      />
    </div>
  );
};

export default AgentInstallation;
